import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Leaderboard from './Leaderboard';

const HomePage = () => {
  const { user, isGuest } = useAuth();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [pendingQuizType, setPendingQuizType] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/quiz/languages');
      setLanguages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
      setError('Failed to load languages. Please refresh the page.');
    }
  };

  const handleRandomQuiz = async () => {
    if (!selectedLanguage) {
      setPendingQuizType('random');
      setShowLanguageDialog(true);
      return;
    }

    setLoading(true);
    navigate(`/quiz/random/${selectedLanguage}`);
  };

  const handleGenZQuiz = async () => {
    if (!selectedLanguage) {
      setPendingQuizType('genz');
      setShowLanguageDialog(true);
      return;
    }

    setLoading(true);
    navigate(`/quiz/genz/${selectedLanguage}`);
  };

  const handlePopularQuiz = async () => {
    if (!selectedLanguage) {
      setPendingQuizType('popular');
      setShowLanguageDialog(true);
      return;
    }

    setLoading(true);
    navigate(`/quiz/popular/${selectedLanguage}`);
  };

  const handleLanguageSelect = language => {
    setSelectedLanguage(language);
    setShowLanguageDialog(false);

    if (pendingQuizType) {
      setLoading(true);
      if (pendingQuizType === 'random') {
        navigate(`/quiz/random/${language}`);
      } else if (pendingQuizType === 'genz') {
        navigate(`/quiz/genz/${language}`);
      } else if (pendingQuizType === 'popular') {
        navigate(`/quiz/popular/${language}`);
      }
      setPendingQuizType(null);
    }
  };

  const closeLanguageDialog = () => {
    setShowLanguageDialog(false);
    setPendingQuizType(null);
  };

  // Show error state
  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '2rem 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className='container' style={{ textAlign: 'center' }}>
          <h1 style={{ color: 'white', marginBottom: '1rem' }}>⚠️ Error</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem' }}>{error}</p>
          <button onClick={() => window.location.reload()} className='btn btn-primary'>
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '2rem 0' }}>
      <div className='container'>
        {/* Header with Auth Status */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '2rem',
            gap: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: '2.5rem', marginRight: '1rem' }}>🎬</span>
            <div>
              <h1
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: 'white',
                  margin: 0,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                Movie Quiz Generator
              </h1>
              <p style={{ fontSize: '1rem', color: '#ff8c00', margin: 0, fontWeight: '500' }}>
                {isGuest
                  ? 'Playing as Guest'
                  : `Welcome back, ${user?.name?.split(' ')[0] || 'User'}!`}
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <button
              onClick={() => navigate('/profile')}
              className='btn btn-secondary'
              style={{
                fontSize: '0.9rem',
                padding: '0.75rem 1.5rem',
                whiteSpace: 'nowrap',
                minWidth: '140px',
              }}
            >
              {isGuest ? '📊 Guest Scores' : '👤 Profile'}
            </button>
            {isGuest ? (
              <a
                href='/api/auth/google'
                className='btn btn-google'
                style={{
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  padding: '0.75rem 1.5rem',
                  whiteSpace: 'nowrap',
                  minWidth: '180px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                    fill='#4285F4'
                  />
                  <path
                    d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                    fill='#34A853'
                  />
                  <path
                    d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                    fill='#FBBC05'
                  />
                  <path
                    d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                    fill='#EA4335'
                  />
                </svg>
                Sign in with Google
              </a>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {user?.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '50%',
                      border: '2px solid var(--royal-orange, #ff8c00)',
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className='text-center mb-8'>
          <p
            style={{
              fontSize: '1.2rem',
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '600px',
              margin: '0 auto',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            }}
          >
            Test your movie knowledge with AI-generated quizzes. Choose from specific movies or take
            random quizzes in different languages!
          </p>
        </div>

        <div className='card mb-8'>
          <h2 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
            Choose Your Language
          </h2>
          <select
            value={selectedLanguage}
            onChange={e => setSelectedLanguage(e.target.value)}
            className='form-input'
            style={{ marginBottom: '1.5rem', fontSize: '1rem' }}
          >
            <option value=''>Select Language</option>
            {languages.map(language => (
              <option key={language} value={language}>
                {language.charAt(0) + language.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
          {selectedLanguage && (
            <p
              style={{
                color: '#22c55e',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                textAlign: 'center',
              }}
            >
              ✓ Language selected:{' '}
              {selectedLanguage.charAt(0) + selectedLanguage.slice(1).toLowerCase()}
            </p>
          )}
        </div>

        <div className='grid grid-3 mb-8'>
          <div className='card text-center'>
            <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>⚡</span>
            <h3 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
              Quick Random Quiz
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1.5rem' }}>
              Jump straight into a quiz with a randomly selected movie from your chosen language.
            </p>
            <button
              onClick={handleRandomQuiz}
              disabled={loading}
              className='btn btn-primary'
              style={{ width: '100%' }}
            >
              {loading ? <div className='loading-spinner'></div> : 'Start Random Quiz'}
            </button>
          </div>

          <div className='card text-center'>
            <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>🔥</span>
            <h3 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
              Popular Movies Quiz
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1.5rem' }}>
              Test your knowledge on the most popular movies from 2000+ based on Wikipedia
              popularity!
            </p>
            <button
              onClick={handlePopularQuiz}
              disabled={loading}
              className='btn btn-accent'
              style={{ width: '100%' }}
            >
              {loading ? <div className='loading-spinner'></div> : 'Start Popular Quiz'}
            </button>
          </div>

          <div className='card text-center'>
            <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>🎯</span>
            <h3 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
              GenZ Special Quiz
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '1.5rem' }}>
              Test your knowledge on movies from 2000 onwards - perfect for the GenZ generation!
            </p>
            <button
              onClick={handleGenZQuiz}
              disabled={loading}
              className='btn btn-secondary'
              style={{ width: '100%' }}
            >
              {loading ? <div className='loading-spinner'></div> : 'Start GenZ Quiz'}
            </button>
          </div>
        </div>

        {/* Language Selection Dialog */}
        {showLanguageDialog && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              className='card'
              style={{
                maxWidth: '400px',
                width: '90%',
                position: 'relative',
              }}
            >
              <button
                onClick={closeLanguageDialog}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
              <h3
                style={{
                  fontSize: '1.5rem',
                  color: 'white',
                  marginBottom: '1rem',
                  textAlign: 'center',
                }}
              >
                🌍 Select Your Language
              </h3>
              <p
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                }}
              >
                Please choose a language to start your{' '}
                {pendingQuizType === 'genz'
                  ? 'GenZ'
                  : pendingQuizType === 'popular'
                    ? 'Popular'
                    : 'random'}{' '}
                quiz:
              </p>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {languages.map(language => (
                  <button
                    key={language}
                    onClick={() => handleLanguageSelect(language)}
                    className='btn btn-secondary'
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      padding: '0.75rem 1rem',
                    }}
                  >
                    {language.charAt(0) + language.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {!loading && <Leaderboard />}

        <div className='card'>
          <h3
            style={{
              fontSize: '1.5rem',
              color: 'white',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}
          >
            Features
          </h3>
          <div className='grid grid-3'>
            <div className='text-center'>
              <div
                style={{
                  width: '3rem',
                  height: '3rem',
                  background: 'rgba(34, 197, 94, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                }}
              >
                <span style={{ color: '#22c55e', fontWeight: 'bold' }}>AI</span>
              </div>
              <h4 style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>
                AI-Powered
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                Questions generated by ChatGPT using real movie metadata
              </p>
            </div>
            <div className='text-center'>
              <div
                style={{
                  width: '3rem',
                  height: '3rem',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                }}
              >
                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>NS</span>
              </div>
              <h4 style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>
                No Spoilers
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                Only factual questions about cast, crew, and production
              </p>
            </div>
            <div className='text-center'>
              <div
                style={{
                  width: '3rem',
                  height: '3rem',
                  background: 'rgba(59, 130, 246, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                }}
              >
                <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>ML</span>
              </div>
              <h4 style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem' }}>
                Multi-Language
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                Movies from various languages and regions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
